import { PayloadAction } from "@reduxjs/toolkit";
import { put, takeLatest, call, all, select } from "redux-saga/effects";

import { BannerStatus } from "@plex/react-components";

import { HttpStatusCodes } from "infrastructure/http";
import { commonMessages } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { ITerm } from "ui/screens/plex-dictionary";
import { displayMessages } from "ui/screens/translation-managers";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { extractApiErrorMessage } from "ui/shared/models/common";

import { IMessage } from "../message-relationships/MessageRelationships.state";

import { dictionaryLinksActions } from "./DictionaryLinks.actions";
import { getAllTerms, getAssociatedTerms, addAssociatedTerm, deleteAssociatedTerm, getMessageDetails } from "./DictionaryLinks.api";
import { IAssociatedMessage, IDictionaryLinksState } from "./DictionaryLinks.state";

function* loadActualTermsExecute({ payload }: PayloadAction<IPickerSearchCriterion<ITerm>>) {
  try {
    let terms: ITerm[] = yield call(getAllTerms, payload.query.query);
    terms = applyPickerFilter(terms, payload, row => row.term);
    yield put(dictionaryLinksActions.loadPickerTermsCompleted(terms));
  } catch (error) {
    yield put(dictionaryLinksActions.loadPickerTermsFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* loadMessageVersionExecute({ payload }: PayloadAction<string>) {
  try {
    const messageDetails: IMessage = yield call(getMessageDetails, payload);
    yield put(dictionaryLinksActions.initializeDictionaryLinksCompleted(messageDetails.version));
  } catch (error) {
    yield put(dictionaryLinksActions.initializeDictionaryLinksFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* loadAssociatedTermsExecute({ payload }: PayloadAction<string>) {
  try {
    const terms: ITerm[] = yield call(getAssociatedTerms, payload);
    yield put(dictionaryLinksActions.loadAssociatedTermsCompleted(terms));
  } catch (error) {
    yield put(dictionaryLinksActions.loadAssociatedTermsFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* addAssociatedTermExecute({ payload }: PayloadAction<{ message: IAssociatedMessage; redirect?: boolean }>) {
  const message = payload.message;
  const state: IDictionaryLinksState = yield select(state => state.dictionaryLinks);

  try {
    yield call(addAssociatedTerm, message.messageId, message.term.termId, state.messageVersion);
    yield put(
      dictionaryLinksActions.addAssociatedTermCompleted({
        message: notification.create(BannerStatus.success).useMessageId(displayMessages.info("addAssociatedTermCompleted")),
        term: message.term,
        redirect: payload.redirect
      })
    );
  } catch (error) {
    yield put(
      dictionaryLinksActions.addAssociatedTermFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error(error).useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error(error).useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* deleteAssociatedTermExecute({ payload }: PayloadAction<IAssociatedMessage>) {
  const state: IDictionaryLinksState = yield select(state => state.dictionaryLinks);

  try {
    yield call(deleteAssociatedTerm, payload.messageId, payload.term.termId, state.messageVersion);
    yield put(
      dictionaryLinksActions.deleteAssociatedTermCompleted({
        message: notification.create(BannerStatus.success).useMessageId(displayMessages.info("deleteAssociatedTermCompleted")),
        removedTerm: payload.term
      })
    );
  } catch (error) {
    yield put(
      dictionaryLinksActions.deleteAssociatedTermFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error(error).useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error(error).useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* actionWatcher() {
  yield takeLatest(dictionaryLinksActions.loadAssociatedTerms.toString(), loadAssociatedTermsExecute);
  yield takeLatest(dictionaryLinksActions.loadPickerTerms.toString(), loadActualTermsExecute);
  yield takeLatest(dictionaryLinksActions.initializeDictionaryLinks.toString(), loadMessageVersionExecute);
  yield takeLatest(dictionaryLinksActions.addAssociatedTerm.toString(), addAssociatedTermExecute);
  yield takeLatest(dictionaryLinksActions.deleteAssociatedTerm.toString(), deleteAssociatedTermExecute);
}

export function* appSaga() {
  yield all([actionWatcher()]);
}
